<script setup>
import { ref } from "vue"
import UserForm from "./UserForm.vue"
import TrainAddForm from "./TrainAddForm.vue"
import TrainEditForm from "./TrainEditForm.vue"

import api from "@/services/api"
import { useICPStore } from "@/stores/icpStore"
import { notifyError, notifySuccess } from "@/services/toastService"

// Tabs & dialog
const adminTab = ref(0) // 0 = Users, 1 = Trains
const dialog = ref(false)

// User state
const userAction = ref("add")
const userForm = ref({ id: "", operatorProfileId: "", username: "", password: "", role: "" })
const isSavingUser = ref(false)
const isSavingTrain = ref(false)
// Train state
const trainAction = ref("add")

// ===== Users =====
async function handleUserSave(formData) {
  isSavingUser.value = true

  if (userAction.value === "add") {
    useICPStore().ICP.dispatch.query.queryUserListV1({
      offset: "0",
      limit: "1000",
      callback: async function (data) {
        if (data.rsp === "0") {
          let userExists = data.list.some(usr => usr.isdn === formData.username)

          if (userExists) {
            try {
              await api.post("/mcx/registerOperator", formData)
              notifySuccess("User created successfully.")
            } catch (err) {
              notifyError("Error registering user: " + err.response?.data)
            }
          } else {
            notifyError("User must be created in the LTE server first.")
          }
        }
        isSavingUser.value = false
      }
    })
  } else {
    dialog.value = false
    isSavingUser.value = false
  }
}

// ===== Trains =====
async function handleTrainSave(formData) {
  isSavingTrain.value = true;

  const proceedSave = async () => {
    useICPStore().ICP.dispatch.query.queryUserListV1({
      offset: "0",
      limit: "1000",
      callback: async function (data) {
        if (data.rsp === "0") {
          let isdnCab1Exists = data.list.some(usr => usr.isdn === formData.isdnCab1);
          let isdnCab2Exists = data.list.some(usr => usr.isdn === formData.isdnCab2);

          if (isdnCab1Exists && isdnCab2Exists) {
            try {
              if (trainAction.value === "add") {
                await api.post("/train/registerTrain", formData);
                notifySuccess("Train registered successfully.");
              } else if (trainAction.value === "edit") {
                await api.put("/train/updateTrainIsdn", formData); // <-- PUT request for editing
                notifySuccess("Train updated successfully.");
              }
            } catch (err) {
              notifyError(
                  trainAction.value === "add"
                      ? "Error registering train: " + err.response?.data
                      : "Error updating train: " + err.response?.data
              );
            }
          } else {
            notifyError("Both ISDN Cab1 and ISDN Cab2 must exist in the LTE server first.");
          }
        } else {
          notifyError("Failed to query train list.");
        }
        isSavingTrain.value = false;
      }
    });
  };

  if (trainAction.value === "add" || trainAction.value === "edit") {
    proceedSave();
  } else {
    dialog.value = false;
    isSavingTrain.value = false;
  }
}

</script>

<template>
  <v-dialog v-model="dialog" max-width="900" persistent>
    <template #activator="{ props }">
      <v-btn class="w-100 mb-2" prepend-icon="mdi-cog" v-bind="props">
        Manage
      </v-btn>
    </template>

    <v-card>
      <v-tabs v-model="adminTab" bg-color="grey-lighten-4" grow>
        <v-tab :value="0">Users</v-tab>
        <v-tab :value="1">Trains</v-tab>
      </v-tabs>

      <v-window v-model="adminTab">
        <!-- ===== Users Tab ===== -->
        <v-window-item :value="0">
          <v-card-text>
            <v-row no-gutters>
              <v-col cols="12" class="pa-4">
                <div class="text-h6 mb-3">
                  {{ userAction === "add" ? "Register User" : "Change Password" }}
                </div>
                <UserForm
                    :mode="userAction"
                    v-model="userForm"
                    :loading="isSavingUser"
                    @save="handleUserSave"
                    @cancel="dialog = false"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-window-item>

        <!-- ===== Trains Tab ===== -->
        <v-window-item :value="1">
          <v-card-text>
            <v-row no-gutters>
              <!-- Sidebar buttons -->
              <v-col cols="1" class="pa-0">
                <v-sheet
                    class="pa-2 h-100 d-flex flex-column align-center justify-center"
                    elevation="1"
                    style="min-width: 70px;"
                >
                  <v-btn
                      :color="trainAction === 'add' ? 'grey' : 'grey-darken'"
                      icon="mdi-plus"
                      class="mb-4"
                      @click="trainAction = 'add'"
                  />
                  <v-btn
                      :color="trainAction === 'edit' ? 'grey' : 'grey-darken'"
                      icon="mdi-pencil"
                      @click="trainAction = 'edit'"
                  />
                </v-sheet>
              </v-col>

              <!-- Form area -->
              <v-col cols="11" class="pa-4">
                <div class="text-h6 mb-3">
                  {{ trainAction === "add" ? "Add Train" : "Edit Train" }}
                </div>
                <TrainAddForm
                    v-if="trainAction === 'add'"
                    :loading="isSavingTrain"
                    @save="handleTrainSave"
                    @cancel="dialog = false"
                />
                <TrainEditForm
                    v-else
                    :loading="isSavingTrain"
                    @save="handleTrainSave"
                    @cancel="dialog = false"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-window-item>
      </v-window>
    </v-card>
  </v-dialog>
</template>
